import React from 'react';
import { TrendingUp, Eye, DollarSign, Target } from 'lucide-react';

const MarketingEffectiveness: React.FC = () => {
  const campaigns = [
    {
      name: 'Summer Sale Window Display',
      reach: 2340,
      engagement: 892,
      conversions: 234,
      revenue: '$12,450',
      roi: 340
    },
    {
      name: 'Email Promotion: New Arrivals',
      reach: 1850,
      engagement: 645,
      conversions: 187,
      revenue: '$8,920',
      roi: 285
    },
    {
      name: 'Social Media: Instagram Story',
      reach: 4200,
      engagement: 1240,
      conversions: 156,
      revenue: '$6,780',
      roi: 210
    }
  ];

  const displays = [
    { location: 'Front Window', views: 2340, interactions: 892, conversion: 38 },
    { location: 'Entry Endcap', views: 1850, interactions: 645, conversion: 35 },
    { location: 'Back Wall Feature', views: 980, interactions: 287, conversion: 29 }
  ];

  const metrics = [
    { label: 'Campaign Reach', value: '8.4K', change: '+24%', icon: Eye },
    { label: 'Engagement Rate', value: '33%', change: '+8%', icon: Target },
    { label: 'Campaign Revenue', value: '$28K', change: '+42%', icon: DollarSign },
    { label: 'Avg. ROI', value: '278%', change: '+15%', icon: TrendingUp }
  ];

  return (
    <div className="p-6" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Marketing Effectiveness
        </h1>
        <p style={{ color: 'var(--sca-muted)' }}>
          Measure campaign impact and in-store marketing performance
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metrics.map((metric, idx) => (
          <div key={idx} className="card">
            <div className="flex items-center justify-between mb-3">
              <metric.icon size={24} style={{ color: 'var(--sca-teal)' }} />
              <span className="text-xs px-2 py-1 rounded-full" style={{
                backgroundColor: 'var(--sca-badge-bg)',
                color: 'var(--sca-teal)'
              }}>
                {metric.change}
              </span>
            </div>
            <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
              {metric.value}
            </div>
            <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
              {metric.label}
            </div>
          </div>
        ))}
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Campaign Performance
        </h3>
        <div className="space-y-4">
          {campaigns.map((campaign, idx) => (
            <div
              key={idx}
              className="p-4 rounded-lg"
              style={{ backgroundColor: 'var(--sca-hover)', border: '1px solid var(--sca-border)' }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
                    {campaign.name}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Reach</div>
                      <div className="text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                        {campaign.reach.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Engagement</div>
                      <div className="text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                        {campaign.engagement.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Conversions</div>
                      <div className="text-sm font-semibold" style={{ color: 'var(--sca-teal)' }}>
                        {campaign.conversions}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Revenue</div>
                      <div className="text-sm font-semibold" style={{ color: 'var(--sca-teal)' }}>
                        {campaign.revenue}
                      </div>
                    </div>
                  </div>
                </div>
                <span
                  className="px-2 py-1 rounded text-xs font-medium ml-3"
                  style={{
                    backgroundColor: 'var(--sca-badge-bg)',
                    color: 'var(--sca-teal)'
                  }}
                >
                  {campaign.roi}% ROI
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Display Performance
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--sca-border)' }}>
                <th className="text-left py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Display Location
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Views
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Interactions
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Conversion Rate
                </th>
              </tr>
            </thead>
            <tbody>
              {displays.map((display, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid var(--sca-border)' }}>
                  <td className="py-3 px-2 text-sm font-medium" style={{ color: 'var(--sca-ink)' }}>
                    {display.location}
                  </td>
                  <td className="py-3 px-2 text-sm text-center" style={{ color: 'var(--sca-muted)' }}>
                    {display.views.toLocaleString()}
                  </td>
                  <td className="py-3 px-2 text-sm text-center" style={{ color: 'var(--sca-muted)' }}>
                    {display.interactions.toLocaleString()}
                  </td>
                  <td className="py-3 px-2 text-sm text-center">
                    <span
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: 'var(--sca-badge-bg)',
                        color: 'var(--sca-teal)'
                      }}
                    >
                      {display.conversion}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MarketingEffectiveness;
